import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PROJECTS, CLIENTS } from "../lib/content";
import SEO from "../components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };

const INSTALLATION_SLUGS = ["thornybush-game-lodge", "kruger-park-lodge", "likweti-bushveld-farm-estate", "karingani", "kubili-house", "buhala-lodge", "kambaku-river-lodge"];
const INSTALLATIONS = INSTALLATION_SLUGS
  .map((s) => PROJECTS.find((p) => p.slug === s))
  .filter(Boolean);

const INSTALLATION_SLUG_SET = new Set(INSTALLATION_SLUGS);
const MORE_PARTNERS = (() => {
  // Build {name, website} pairs from projects + clients, de-duped against installations.
  const fromProjects = PROJECTS
    .filter((p) => !INSTALLATION_SLUG_SET.has(p.slug))
    .map((p) => ({ name: p.name, website: p.website || null }));
  const fromClients = CLIENTS
    .filter((c) =>
      !/Active Mining|Citrus Estate|Macadamia|Sugar|Avocado|Private Utility|Local Municipality|Boutique|Conference/i.test(
        c.name
      )
    )
    .map((c) => ({ name: c.name, website: c.website || null }));
  const installationNames = new Set(INSTALLATIONS.map((f) => f.name.toLowerCase()));
  const seen = new Set(installationNames);
  const out = [];
  for (const item of [...fromProjects, ...fromClients]) {
    const key = item.name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      // If a later entry has a website and the earlier one didn't, the first wins
      // — but since we walk projects first and project entries carry websites,
      // this is the correct precedence by design.
      out.push(item);
    }
  }
  return out;
})();

// JSON-LD: every client we name on the page with a verified URL.
// Uses Schema.org Organization with `knowsAbout` so Google associates HidroVerse
// with the full set of luxury hospitality brands we engineer water for.
// This is the primary SEO win from the outbound links — far more than the raw href.
const NAMED_CLIENT_LD = (() => {
  const everyone = [
    ...PROJECTS.map((p) => ({ name: p.name, website: p.website || null })),
    ...CLIENTS.map((c) => ({ name: c.name, website: c.website || null })),
  ].filter((x) => x.website);
  const seenLd = new Set();
  const unique = [];
  for (const e of everyone) {
    const k = e.name.toLowerCase();
    if (seenLd.has(k)) continue;
    seenLd.add(k);
    unique.push(e);
  }
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "HidroVerse",
    url: "https://colors-25.preview.emergentagent.com/",
    knowsAbout: unique.map((u) => ({
      "@type": "Organization",
      name: u.name,
      url: u.website,
    })),
  };
})();

// =====================================================================
// Lightbox — fullscreen image viewer with keyboard nav
// =====================================================================
function Lightbox({ images, index, title, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  if (!images || images.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — image ${index + 1} of ${images.length}`}
      data-testid="lightbox"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(7,10,13,0.94)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "5vh 5vw",
      }}
    >
      {/* Close */}
      <button
        type="button"
        aria-label="Close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          width: 44,
          height: 44,
          borderRadius: 999,
          background: "rgba(245,247,250,0.08)",
          border: "1px solid rgba(245,247,250,0.18)",
          color: "#F5F7FA",
          fontSize: 20,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        ×
      </button>

      {/* Caption */}
      <div
        style={{
          position: "absolute",
          top: 28,
          left: 28,
          fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
          fontSize: "0.62rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: "rgba(245,247,250,0.7)",
        }}
      >
        <span style={{ color: "#1FB6C1" }}>● </span>
        {title} — {index + 1} / {images.length}
      </div>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Previous image"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          data-testid="lightbox-prev"
          style={{
            position: "absolute",
            left: 24,
            top: "50%",
            transform: "translateY(-50%)",
            width: 52,
            height: 52,
            borderRadius: 999,
            background: "rgba(245,247,250,0.08)",
            border: "1px solid rgba(245,247,250,0.18)",
            color: "#F5F7FA",
            fontSize: 22,
            cursor: "pointer",
          }}
        >
          ‹
        </button>
      )}

      {/* Image */}
      <img
        src={images[index]}
        alt={`${title} ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        loading="lazy"
        decoding="async"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          borderRadius: 6,
          boxShadow: "0 30px 80px -10px rgba(0,0,0,0.6)",
        }}
      />

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Next image"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          data-testid="lightbox-next"
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            width: 52,
            height: 52,
            borderRadius: 999,
            background: "rgba(245,247,250,0.08)",
            border: "1px solid rgba(245,247,250,0.18)",
            color: "#F5F7FA",
            fontSize: 22,
            cursor: "pointer",
          }}
        >
          ›
        </button>
      )}
    </div>
  );
}

// =====================================================================
// Labeled body block — THE CLIENT · SYSTEM
// =====================================================================
function LabelRow({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] md:grid-cols-[130px_1fr] gap-x-4 md:gap-x-5 gap-y-1.5 items-start">
      <div
        className="font-mono uppercase pt-1"
        style={{
          fontSize: "0.56rem",
          letterSpacing: "0.22em",
          color: "rgba(12,42,64,0.55)",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <p
        className="font-light leading-relaxed"
        style={{ fontSize: "0.88rem", color: "#1A1F25" }}
      >
        {children}
      </p>
    </div>
  );
}

function ClientLogo({ src, name, tone, size = 48, ratio = 2, slug }) {
  const onDark = tone === "light";
  return (
    <div
      className="shrink-0 flex items-center justify-center"
      style={{
        padding: "10px 14px",
        borderRadius: 6,
        background: onDark ? "#0C2A40" : "#FFFFFF",
        border: onDark ? "1px solid #0C2A40" : "1px solid rgba(12,42,64,0.10)",
      }}
      data-testid={`client-logo-${slug}`}
    >
      <img
        src={src}
        alt={`${name} logo`}
        className="block"
        width={Math.round(size * ratio)}
        height={size}
        style={{ width: `min(${Math.round(size * ratio)}px, 34vw)`, height: "auto", aspectRatio: String(ratio) }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export default function Projects() {
  const [box, setBox] = useState(null); // { images, index, title }

  const open = useCallback((images, title, startIndex = 0) => {
    if (!images || images.length === 0) return;
    setBox({ images, title, index: startIndex });
  }, []);
  const close = useCallback(() => setBox(null), []);
  const prev = useCallback(() => {
    setBox((b) => b && { ...b, index: (b.index - 1 + b.images.length) % b.images.length });
  }, []);
  const next = useCallback(() => {
    setBox((b) => b && { ...b, index: (b.index + 1) % b.images.length });
  }, []);

  return (
    <div data-testid="projects-page" style={{ background: "#FAFAF7" }}>
      <SEO
        title="Portfolio"
        description="A selection of the lodges, golf estates and resort estates HidroVerse engineers water for — with their own words on what we delivered. Names and quotes drawn directly from the company profile."
        path="/projects"
        ldJson={NAMED_CLIENT_LD}
      />

      {/* ============================================================
          HERO
          ============================================================ */}
      <section
        className="relative pt-36 md:pt-44 pb-20 md:pb-24 px-6 md:px-12 lg:px-20"
        style={{ background: "#070A0D", color: "#F5F7FA" }}
        data-testid="portfolio-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/mockups/14_projects_hero.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.32,
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,10,13,0.55) 0%, rgba(7,10,13,0.82) 70%, #070A0D 100%)",
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <div
            className="font-mono uppercase mb-5"
            style={{ fontSize: "0.62rem", letterSpacing: "0.28em", color: "#1FB6C1" }}
          >
            Portfolio
          </div>
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(2.4rem, 6vw, 5.2rem)",
              lineHeight: 1.06,
              letterSpacing: "-0.025em",
              fontWeight: 300,
              maxWidth: "20ch",
            }}
          >
            The sites<br />
            <span style={{ color: "#1FB6C1", fontWeight: 600 }}>we keep running.</span>
          </h1>
          <p
            className="mt-8 max-w-2xl font-light"
            style={{ fontSize: "1.02rem", color: "#9AA7B4", lineHeight: 1.6 }}
          >
            A selection of our work, and what our clients had to say about it.
          </p>
        </div>
      </section>

      {/* ============================================================
          INSTALLATIONS — compact 3-block case studies, clickable photos
          ============================================================ */}
      {INSTALLATIONS.length > 0 && (
        <section
          className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
          style={{ background: "#FFFFFF" }}
          data-testid="portfolio-installations"
        >
          <div className="max-w-[1400px] mx-auto">
            <div
              className="font-mono uppercase mb-3"
              style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1", fontWeight: 700 }}
            >
              Selected installs
            </div>
            <h2
              className="font-display mb-4"
              style={{
                fontSize: "clamp(1.7rem, 3vw, 2.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
                fontWeight: 600,
                color: "#0C2A40",
                maxWidth: "22ch",
              }}
            >
              Premium brands. Engineered water.
            </h2>
            <p
              className="font-light leading-relaxed mb-10 md:mb-12 max-w-xl"
              style={{ fontSize: "0.92rem", color: "rgba(12,42,64,0.7)" }}
            >
              Who the client is, and the system HidroVerse engineered for them.
            </p>

            <div className="grid gap-10 md:gap-14">
              {INSTALLATIONS.map((p, i) => {
                const reverse = i % 2 === 1;
                const gallery = p.gallery || (p.img ? [p.img] : []);
                const hasPhoto = gallery.length > 0;
                return (
                  <motion.article
                    key={p.slug}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-60px" }}
                    variants={fadeUp}
                    transition={{ delay: i * 0.04 }}
                    className={hasPhoto ? "grid md:grid-cols-12 gap-7 md:gap-10 items-center" : "max-w-3xl"}
                    data-testid={`installation-${p.slug}`}
                  >
                    {/* PHOTO SIDE — clickable, opens lightbox */}
                    {hasPhoto && (
                      <div className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                        <button
                          type="button"
                          onClick={() => open(gallery, p.name, 0)}
                          data-testid={`install-photo-${p.slug}`}
                          className="group relative overflow-hidden block w-full text-left"
                          style={{
                            borderRadius: 8,
                            boxShadow:
                              "0 1px 2px rgba(12,42,64,0.06), 0 24px 50px -18px rgba(12,42,64,0.18)",
                            aspectRatio: "16 / 10",
                            cursor: "zoom-in",
                            background: "#0C2A40",
                          }}
                          aria-label={`View ${p.name} gallery`}
                        >
                          <img
                            src={gallery[0]}
                            alt={`${p.name} — HidroVerse install`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            style={{ filter: "saturate(1.02) contrast(1.02)" }} loading="lazy" decoding="async"/>
                          {/* hover overlay */}
                          <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{
                              background:
                                "linear-gradient(180deg, rgba(7,10,13,0) 40%, rgba(7,10,13,0.55) 100%)",
                            }}
                          />
                          <div
                            className="absolute left-3.5 bottom-3.5 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full font-mono uppercase"
                            style={{
                              background: "rgba(7,10,13,0.72)",
                              backdropFilter: "blur(8px)",
                              WebkitBackdropFilter: "blur(8px)",
                              border: "1px solid rgba(245,247,250,0.14)",
                              fontSize: "0.5rem",
                              letterSpacing: "0.22em",
                              color: "rgba(245,247,250,0.94)",
                            }}
                          >
                            <span style={{ color: "#1FB6C1", fontSize: "0.62rem" }}>●</span>
                            {p.name}
                          </div>
                          {gallery.length > 1 && (
                            <div
                              className="absolute right-3.5 bottom-3.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full font-mono uppercase"
                              style={{
                                background: "rgba(31,182,193,0.92)",
                                fontSize: "0.5rem",
                                letterSpacing: "0.2em",
                                color: "#070A0D",
                                fontWeight: 700,
                              }}
                            >
                              +{gallery.length - 1} photos
                            </div>
                          )}
                        </button>

                        {/* Gallery thumb strip — every thumb opens lightbox at its index */}
                        {gallery.length > 1 && (
                          <div
                            className="mt-2.5 grid gap-2"
                            style={{
                              gridTemplateColumns: `repeat(${Math.min(gallery.length - 1, 4)}, minmax(0, 1fr))`,
                            }}
                            data-testid={`install-thumbs-${p.slug}`}
                          >
                            {gallery.slice(1, 5).map((src, idx) => {
                              const overflowCount = gallery.length - 5;
                              const isLastWithOverflow = idx === 3 && overflowCount > 0;
                              return (
                                <button
                                  type="button"
                                  key={src}
                                  onClick={() => open(gallery, p.name, idx + 1)}
                                  aria-label={`View ${p.name} image ${idx + 2}`}
                                  className="relative overflow-hidden block w-full group/thumb"
                                  style={{
                                    borderRadius: 6,
                                    aspectRatio: "4 / 3",
                                    border: "1px solid rgba(12,42,64,0.08)",
                                    cursor: "zoom-in",
                                    background: "#0C2A40",
                                  }}
                                >
                                  <img
                                    src={src}
                                    alt=""
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-[1.05]"
                                    style={{ filter: "saturate(1.02) contrast(1.02)" }} loading="lazy" decoding="async"/>
                                  {isLastWithOverflow && (
                                    <div
                                      className="absolute inset-0 flex items-center justify-center font-mono uppercase"
                                      style={{
                                        background: "rgba(7,10,13,0.62)",
                                        backdropFilter: "blur(2px)",
                                        WebkitBackdropFilter: "blur(2px)",
                                        color: "#F5F7FA",
                                        fontSize: "0.7rem",
                                        letterSpacing: "0.18em",
                                        fontWeight: 700,
                                      }}
                                    >
                                      +{overflowCount}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* COPY SIDE */}
                    <div className={hasPhoto ? `md:col-span-6 ${reverse ? "md:order-1" : ""}` : ""}>
                      <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                      <div
                        className="font-mono uppercase mb-2.5"
                        style={{ fontSize: "0.58rem", letterSpacing: "0.24em", color: "#1FB6C1", fontWeight: 700 }}
                      >
                        {p.type}
                      </div>
                      <h3
                        className="font-display mb-1"
                        style={{
                          fontSize: "clamp(1.3rem, 1.9vw, 1.7rem)",
                          lineHeight: 1.12,
                          letterSpacing: "-0.013em",
                          color: "#0C2A40",
                          fontWeight: 600,
                        }}
                      >
                        {p.name}
                      </h3>
                      <div
                        className="font-light mb-2"
                        style={{ fontSize: "0.8rem", color: "rgba(12,42,64,0.55)" }}
                      >
                        {p.region}
                      </div>
                      {p.website && (
                        <a
                          href={p.website}
                          target="_blank"
                          rel="noopener"
                          className="inline-flex items-center gap-1.5 font-mono uppercase mb-5 transition-colors hover:text-[#1FB6C1]"
                          style={{
                            fontSize: "0.58rem",
                            letterSpacing: "0.24em",
                            color: "rgba(12,42,64,0.7)",
                            fontWeight: 700,
                          }}
                          data-testid={`project-website-${p.slug}`}
                        >
                          Visit site <span aria-hidden>↗</span>
                        </a>
                      )}
                      {!p.website && <div className="mb-5" />}
                      </div>
                      {p.logo && <ClientLogo src={p.logo} name={p.name} tone={p.logoTone} size={p.logoSize} ratio={p.logoRatio} slug={p.slug} />}
                      </div>

                      <div className="flex flex-col gap-3.5">
                        {p.client ? (
                          <LabelRow label="The client">{p.client}</LabelRow>
                        ) : null}
                        {p.system ? (
                          <LabelRow label="System">{p.system}</LabelRow>
                        ) : null}
                        {/* Fallback: if no structured fields, show body once */}
                        {!p.client && !p.system && p.body ? (
                          <p
                            className="font-light leading-relaxed"
                            style={{ fontSize: "0.9rem", color: "#1A1F25" }}
                          >
                            {p.body}
                          </p>
                        ) : null}
                      </div>

                      {p.quote && (
                        <blockquote
                          className="pl-4 mt-5"
                          style={{ borderLeft: "2px solid #1FB6C1" }}
                        >
                          <p
                            className="font-display italic"
                            style={{
                              fontSize: "0.88rem",
                              lineHeight: 1.55,
                              color: "#0C2A40",
                              fontWeight: 300,
                            }}
                          >
                            "{p.quote}"
                          </p>
                          {p.by && (
                            <div
                              className="mt-2.5 font-mono uppercase"
                              style={{ fontSize: "0.56rem", letterSpacing: "0.24em", color: "rgba(12,42,64,0.65)", fontWeight: 700 }}
                            >
                              — {p.by}
                            </div>
                          )}
                        </blockquote>
                      )}
                    </div>

                    {/* FULL TESTIMONIAL — distinguished multi-paragraph block.
                        Spans the full row so long testimonials read properly.
                        Rendered verbatim — never editorialised. */}
                    {p.testimonial && p.testimonial.paragraphs && p.testimonial.paragraphs.length > 0 && (
                      <div
                        className="md:col-span-12 mt-2"
                        data-testid={`testimonial-${p.slug}`}
                      >
                        <div
                          className="relative"
                          style={{
                            background: "#FFFFFF",
                            borderTop: "1px solid rgba(12,42,64,0.10)",
                            borderRadius: 8,
                            padding: "clamp(1.5rem, 3vw, 2.5rem) clamp(1.25rem, 2.5vw, 2.25rem)",
                            boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 18px 40px -22px rgba(12,42,64,0.16)",
                          }}
                        >
                          {/* Cyan accent rail */}
                          <div
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: 0,
                              top: "clamp(1.5rem, 3vw, 2.5rem)",
                              bottom: "clamp(1.5rem, 3vw, 2.5rem)",
                              width: 3,
                              background: "#1FB6C1",
                              borderRadius: 2,
                            }}
                          />
                          {/* Eyebrow */}
                          <div
                            className="mb-4 text-[0.78rem] font-semibold"
                            style={{ color: "rgba(12,42,64,0.55)", letterSpacing: "0.04em", paddingLeft: 18 }}
                          >
                            Testimonial · in their words
                          </div>
                          {/* Verbatim paragraphs */}
                          <div style={{ paddingLeft: 18 }}>
                            {p.testimonial.paragraphs.map((para, idx) => (
                              <p
                                key={idx}
                                className="font-light leading-relaxed mb-4 last:mb-0"
                                style={{
                                  fontSize: "clamp(0.95rem, 1.1vw, 1.04rem)",
                                  lineHeight: 1.75,
                                  color: "#1A1F25",
                                }}
                              >
                                {para}
                              </p>
                            ))}
                          </div>
                          {/* Attribution */}
                          {(p.testimonial.attribution || p.testimonial.organization) && (
                            <div
                              className="mt-6 text-[0.84rem] font-semibold"
                              style={{ color: "#0C2A40", letterSpacing: "0.01em", paddingLeft: 18 }}
                            >
                              {p.testimonial.attribution}
                              {p.testimonial.attribution && p.testimonial.organization && (
                                <span aria-hidden style={{ color: "rgba(12,42,64,0.45)" }}> · </span>
                              )}
                              <span style={{ color: "rgba(12,42,64,0.65)", fontWeight: 500 }}>
                                {p.testimonial.organization}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          MORE PARTNERS — scrolling ribbon
          ============================================================ */}
      <section
        className="relative overflow-hidden border-y"
        style={{ background: "#0E1318", borderColor: "rgba(255,255,255,0.06)", color: "#F5F7FA" }}
        data-testid="more-partners"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-14 md:pt-20 pb-8 md:pb-10">
          <div
            className="font-mono uppercase mb-4"
            style={{ fontSize: "0.62rem", letterSpacing: "0.28em", color: "#1FB6C1" }}
          >
            More partners
          </div>
          <h3
            className="font-display"
            style={{
              fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              fontWeight: 500,
              color: "#F5F7FA",
              maxWidth: "30ch",
            }}
          >
            Other sites we engineer water for.
          </h3>
        </div>

        <div className="pb-14 md:pb-20">
          <div className="hv-client-ribbon" data-testid="more-partners-track">
            {[...MORE_PARTNERS, ...MORE_PARTNERS].map((p, i) => (
              p.website ? (
                <a
                  key={`${p.name}-${i}`}
                  href={p.website}
                  target="_blank"
                  rel="noopener"
                  className="hv-client-name transition-colors hover:text-[#1FB6C1]"
                  style={{ color: "rgba(245,247,250,0.78)" }}
                  data-testid={`partner-link-${p.name.replace(/\s+/g, "-").toLowerCase()}-${i}`}
                >
                  {p.name}
                </a>
              ) : (
                <span
                  key={`${p.name}-${i}`}
                  className="hv-client-name"
                  style={{ color: "rgba(245,247,250,0.78)" }}
                >
                  {p.name}
                </span>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          CLOSING CTA
          ============================================================ */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20 text-center"
        style={{ background: "#FAFAF7" }}
      >
        <div className="max-w-3xl mx-auto">
          <h3
            className="font-display mb-5"
            style={{
              fontSize: "clamp(2rem, 4.4vw, 3.4rem)",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              fontWeight: 700,
              color: "#0C2A40",
            }}
          >
            Your site<br />
            <span style={{ color: "#1FB6C1" }}>next.</span>
          </h3>
          <p
            className="max-w-xl mx-auto font-light leading-relaxed mb-10"
            style={{ fontSize: "1rem", color: "rgba(26,31,37,0.75)" }}
          >
            For personalised assistance and solutions, contact our specialists.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: "#1FB6C1",
              color: "#070A0D",
              fontSize: "0.78rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
            data-testid="portfolio-cta"
          >
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {box && (
        <Lightbox
          images={box.images}
          index={box.index}
          title={box.title}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
}
